import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE_NAME, SITE_URL, SITE_LOGO, SITE_TWITTER } from './seoData';

/**
 * Enhanced SEO Head component using react-helmet-async.
 * Handles meta titles, descriptions, Open Graph, Twitter Cards, canonical URLs, and hreflang.
 */
export const SEOHead = ({
  title,
  description,
  keywords,
  path,
  type = 'website',
  image = SITE_LOGO,
  noindex = false,
}) => {
  const location = useLocation();
  const currentPath = path || location.pathname;
  // Ensure canonical URL has no trailing slash except for root
  const canonicalUrl = currentPath === '/'
    ? SITE_URL + '/'
    : `${SITE_URL}${currentPath}`.replace(/\/$/, '');

  // Keep title under 60 chars - don't append site name if already long
  const fullTitle = title
    ? (title.length > 50 ? title : `${title} | ${SITE_NAME}`)
    : `${SITE_NAME} - Free Online Tools`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large'} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang - English default */}
      <link rel="alternate" hrefLang="en-US" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE_TWITTER} />
      <meta name="twitter:creator" content={SITE_TWITTER} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta httpEquiv="content-language" content="en-US" />
    </Helmet>
  );
};

export default SEOHead;
